/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
    hooks as commonHooks,
    widgetUtils as commonWidgetUtils,
    themes as commonThemes,
    components as commonComponents,
    types as commonTypes,
    utils as commonUtils,
} from '@sitebuilder/common';

import { actionCreators } from '../reducers/templateEditor';
import { actionCreators as appActionCreators } from '../reducers/app';
import { actionCreators as themeActionCreators } from '../reducers/themes';

import { EditorPlaceholder } from './Placeholder';

const EditorRootDocument = () => {
    const dispatch = useDispatch();

    const appContext = commonHooks.useAppContext();
    const {
        data,
        document: doc,
        accessToken,
        theme: selectedTheme,
        graphqlClientFactory,
        RootDocument,
        inEditMode,
        Placeholder,
    } = appContext;
    const widgetProperties =
        commonHooks.useWidgetProperties<commonTypes.RootDocumentWidgetPropertiesBase>('root') || {};

    const {
        documentType,
        themes,
        theme,
        isDynamicTheme,
        functionToSelectTheme,
        colorSpace: docColorSpace,
    } = widgetProperties || {};

    const [fontFaces, setFontFaces] = useState<string>('');
    const location = useLocation();
    const search = location.search;

    let selectedThemeName = theme;

    try {
        // eslint-disable-next-line no-new-func
        const transformFunction = Function(`${functionToSelectTheme}`)();
        const themeName = isDynamicTheme && transformFunction(data);

        if (
            typeof themeName === 'string' &&
            (['', 'Default'].includes(themeName) || themes?.map((d) => d.id).includes(themeName))
        ) {
            selectedThemeName = themeName;
        }
    } catch (e) {
        console.log(e);
    }

    useEffect(() => {
        dispatch(themeActionCreators.listThemes(''));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (selectedThemeName && selectedThemeName !== 'Default') {
            dispatch(themeActionCreators.loadTheme(selectedThemeName));
        } else {
            dispatch(themeActionCreators.loadDefaultColorPalettes());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedThemeName]);

    useEffect(() => {
        if (doc && !commonWidgetUtils.hasOtherProperties(data, 'resourceStrings')) {
            const graphqlClient = graphqlClientFactory.createClient(accessToken || '');
            const params = new URLSearchParams(search);
            const profileId = params.get('profileId') || '';
            const locale = params.get('locale') || 'en-US';

            commonUtils
                .fetchPageData(
                    doc,
                    graphqlClient,
                    {},
                    {
                        profileId,
                        locale,
                    }
                )
                .then((pageData) => {
                    if (pageData.data) {
                        dispatch(actionCreators.setData(pageData.data));
                    }

                    if (pageData.metadata) {
                        dispatch(actionCreators.setDocumentMetaData(pageData.metadata));
                    }
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [doc, search, accessToken, dispatch]);

    useEffect(() => {
        if (documentType) {
            dispatch(appActionCreators.setDocumentType(documentType));
        }
    }, [dispatch, documentType]);

    useEffect(() => {
        const colorSpace = docColorSpace || (documentType === 'web' ? 'rgb' : 'cmyk');
        let stylethemeTag;

        if (!selectedThemeName || selectedThemeName === 'Default') {
            const cssVariables = commonWidgetUtils.getCssString(
                commonWidgetUtils.getColorsObj(commonThemes.defaultColors, colorSpace),
                commonThemes.defaultTypography as any,
                commonThemes.componentThemes as any
            );

            const cssClasses = commonWidgetUtils.getTypographyCssClasses(
                commonThemes.defaultTypography
            );

            // also applies css style to style tag with specfied id
            stylethemeTag = document.getElementById('theme-style-tag');
            if (stylethemeTag !== null) {
                stylethemeTag!.textContent = `:root{${cssVariables}}` + cssClasses;
            } else {
                const style = document.getElementsByTagName('style')[0];
                style.textContent = `:root{${cssVariables}}` + cssClasses;
            }
        } else if (selectedThemeName !== 'Default' && selectedTheme) {
            const filteredTheme: any = commonWidgetUtils.getCssTheme(
                selectedTheme,
                colorSpace,
                commonThemes.defaultTypography,
                commonThemes.componentThemes
            );

            if (filteredTheme) {
                const defaultThemeFonts = filteredTheme?.defaultFonts
                    ? [...filteredTheme.defaultFonts]
                    : commonThemes.defaultFonts;
                const fonts = filteredTheme?.userFonts
                    ? [...defaultThemeFonts, ...filteredTheme.userFonts]
                    : [...defaultThemeFonts];

                const cssVariables = commonWidgetUtils.getCssString(
                    {
                        ...(filteredTheme.colors as any),
                        ...commonThemes.defaultColors,
                        ...filteredTheme.defaultColors,
                    },
                    { ...(filteredTheme.typography as any), ...filteredTheme.userTypography },
                    { ...commonThemes.componentThemes, ...filteredTheme.componentThemes }
                );

                const cssClasses = commonWidgetUtils.getTypographyCssClasses({
                    ...(filteredTheme.typography as any),
                    ...filteredTheme.userTypography,
                });
                // also applies css style to style tag with specfied id
                stylethemeTag = document.getElementById('theme-style-tag');
                if (stylethemeTag !== null) {
                    stylethemeTag!.textContent = `:root{${cssVariables}}` + cssClasses;
                } else {
                    const style = document.getElementsByTagName('style')[0];
                    style.textContent = `:root{${cssVariables}}` + cssClasses;
                }

                setFontFaces(commonWidgetUtils.getFontFace(fonts));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTheme, selectedThemeName]);

    if (!doc || !data) {
        return null;
    }

    return (
        <commonComponents.AppContext.AppContextProvider
            {...{
                ...appContext,
                themeSelected: selectedThemeName,
                Placeholder: inEditMode ? EditorPlaceholder : Placeholder,
            }}
        >
            <div
                css={css`
                    ${fontFaces}
                `}
            >
                <RootDocument />
            </div>
        </commonComponents.AppContext.AppContextProvider>
    );
};

export default EditorRootDocument;
